import { useTranslation } from "react-i18next";
import {
  type FieldStatusDto,
  type SourcingEventV2Dto,
} from "../../../../../../autogen/bff-api";
import type { ReadinessLookup, KnownFieldKey } from "../../hooks/useReadinessByField";
import type { PublicEventStep } from "../../wizardSteps";
import { EmptyValue, SummaryField } from "./SummaryField";
import { SummaryFieldGroup } from "./SummaryFieldGroup";
import { SummaryGroupHeader } from "./SummaryGroupHeader";
import { SummarySection } from "./SummarySection";

export const FIELD_KEYS = [
  "TitlePresent",
  "RegulationSet",
  "RegulationSupportsPublishing",
  "EstimatedValueSet",
  "ContractNatureSet",
  "FoaPartSet",
  "FoaPartUsesPublicFlow",
] as const satisfies readonly KnownFieldKey[];

interface OverviewSectionProps {
  event: SourcingEventV2Dto;
  readinessFor: ReadinessLookup;
  onWizardStepNavigate: (step: PublicEventStep) => void;
}

export const OverviewSection = ({
  event,
  readinessFor,
  onWizardStepNavigate,
}: OverviewSectionProps) => {
  const { t } = useTranslation();
  const { eventData } = event;
  const publicContext = event.eventContext.type === "Public" ? event.eventContext : null;

  const fieldStatuses = FIELD_KEYS.map(readinessFor).filter(
    (s): s is FieldStatusDto => s !== undefined,
  );

  return (
    <SummarySection
      title={t("Procurement overview")}
      editTarget={{ kind: "wizard-step", step: "info" }}
      fieldStatuses={fieldStatuses}
      onWizardStepNavigate={onWizardStepNavigate}
    >
      <SummaryGroupHeader isFirst>{t("General information")}</SummaryGroupHeader>
      <SummaryFieldGroup>
        <SummaryField
          label={t("Title")}
          value={eventData.title || <EmptyValue text={t("Not provided")} />}
          status={readinessFor("TitlePresent")}
        />
      </SummaryFieldGroup>

      <SummaryGroupHeader>{t("Value and regulation")}</SummaryGroupHeader>
      <SummaryFieldGroup>
        <SummaryField
          label={t("Governing regulation")}
          value={
            publicContext?.regulation
              ? t(publicContext.regulation)
              : <EmptyValue text={t("Not set")} />
          }
          span="half"
          status={readinessFor("RegulationSet")}
          extraStatuses={[readinessFor("RegulationSupportsPublishing")]}
        />
        <SummaryField
          label={t("Applicable part of the regulation")}
          value={
            publicContext?.foaPart
              ? t(publicContext.foaPart)
              : <EmptyValue text={t("Not classified yet")} />
          }
          span="half"
          status={readinessFor("FoaPartSet")}
        />
        <SummaryField
          label={t("Estimated value")}
          value={
            publicContext?.estimatedValue?.value != null
              ? `${publicContext.estimatedValue.value.toLocaleString("nb-NO")} ${publicContext.estimatedValue.currency}`
              : <EmptyValue text={t("Not set")} />
          }
          span="half"
          status={readinessFor("EstimatedValueSet")}
          extraStatuses={[readinessFor("FoaPartUsesPublicFlow")]}
        />
        <SummaryField
          label={t("Contract type")}
          value={
            publicContext?.contractNature
              ? t(publicContext.contractNature)
              : <EmptyValue text={t("Not set")} />
          }
          span="half"
          status={readinessFor("ContractNatureSet")}
        />
      </SummaryFieldGroup>
    </SummarySection>
  );
};
